/**
 * LDS partial payment display — read-only passthrough from Backend SSOT block.
 * @see services/lds_partial_payment_display_balance.py
 */
import { resolveLdsBackendFinancialSsot } from "./ldsBackendFinancialSsot.js";
import { parseLdsOrderPayments } from "./ldsSplitPaymentsSsot.js";
import { pickSupplementTotal } from "./ldsBaggageDoubleCountGuard.js";

const MONEY_TOL = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseAuditIntegrity(row) {
  const audit = row?.post_payment_checkout_audit ?? row?.postPaymentCheckoutAudit;
  if (!audit || typeof audit !== "object") return {};
  const integrity = audit.integrity;
  return integrity && typeof integrity === "object" ? integrity : {};
}

function pickHandlingFee(order) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  return toNumberOrNull(
    op.handleFee ??
      op.handlingFee ??
      op.serviceFee ??
      order?.handlingfee ??
      order?.handling_fee ??
      order?.handlingFee
  );
}

function pickBaggageFee(order) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  return toNumberOrNull(
    op.baggagePrice ?? order?.baggagePrice ?? order?.public_baggage_total_ils
  );
}

function pickCreditCardFee(order) {
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const otf =
    op?.ourTotalFare && typeof op.ourTotalFare === "object" ? op.ourTotalFare : {};
  const pwc = toNumberOrNull(otf.priceWithCommission ?? op.priceWithCommission);
  const pwccf = toNumberOrNull(
    otf.priceWithCommissionAndCreditCardFee ?? op.priceWithCommissionAndCreditCardFee
  );
  const pwccfRoot = toNumberOrNull(op.priceWithCreditCardFee);
  const totalWithCc = pwccf ?? pwccfRoot;
  const base = pwc ?? toNumberOrNull(op.priceWithHandleFee);
  if (base != null && totalWithCc != null && totalWithCc > base) {
    return round2(totalWithCc - base);
  }
  return null;
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function emitRemainingSsotTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_PAYMENT_REMAINING_SSOT_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} order
 */
export function isLdsPartialPaymentDisplayOrder(order) {
  if (!order || typeof order !== "object") return false;
  if (order.admin_payment_display_state === "PARTIALLY_PAID_PENDING_BALANCE") {
    return true;
  }
  const backend = resolveLdsBackendFinancialSsot(order, { emitTrace: false });
  if (
    backend.total != null &&
    backend.paid > MONEY_TOL &&
    backend.remaining != null &&
    backend.remaining > MONEY_TOL
  ) {
    return true;
  }
  return false;
}

/**
 * @param {object|null|undefined} order
 */
export function resolveLdsPartialPaymentDisplayBalance(order) {
  const empty = {
    displayTotal: null,
    displayPaid: 0,
    displayPaidActual: 0,
    paidCapturedActual: 0,
    displayRemaining: null,
    pendingSettlementAmount: null,
    pendingBankTransferToClose: null,
    linkPaymentAmount: null,
    sourceUsed: "empty_order",
    auditRemaining: null,
    adminRemaining: null,
    expectedRemainingByArithmetic: null,
    mismatchDetected: false,
    mismatchAmount: null,
    handlingFee: null,
    supplement: null,
    creditCardFee: null,
    baggageFee: null,
    supplierNet: null,
    hrCommission: null,
    paymentsCount: 0,
    rawTotalCandidates: [],
    rejectedCandidates: [],
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const backend = resolveLdsBackendFinancialSsot(order, { emitTrace: false });
  const beBlock =
    order.lds_partial_payment_display ?? order.ldsPartialPaymentDisplay;
  const audit = parseAuditIntegrity(order);

  const displayTotal = backend.total;
  const displayPaid = backend.paid;
  const displayRemaining = backend.remaining;
  const pendingSettlementAmount = toNumberOrNull(beBlock?.pending_settlement_amount);
  const pendingBankTransferToClose = toNumberOrNull(
    beBlock?.pending_bank_transfer_to_close
  );

  const result = {
    displayTotal,
    displayPaid,
    displayPaidActual: displayPaid,
    paidCapturedActual: displayPaid,
    displayRemaining,
    pendingSettlementAmount,
    pendingBankTransferToClose,
    linkPaymentAmount:
      toNumberOrNull(beBlock?.link_payment_amount) ?? displayRemaining,
    sourceUsed: backend.totalSource
      ? [backend.totalSource, backend.paidSource, backend.remainingSource]
          .filter(Boolean)
          .join("|")
      : "none",
    auditRemaining: toNumberOrNull(audit.remaining_balance),
    adminRemaining: toNumberOrNull(
      order.admin_remaining_balance ?? order.adminRemainingBalance
    ),
    expectedRemainingByArithmetic: displayRemaining,
    mismatchDetected: Boolean(beBlock?.mismatch_detected),
    mismatchAmount: null,
    handlingFee: pickHandlingFee(order),
    supplement: pickSupplementTotal(order),
    creditCardFee: pickCreditCardFee(order),
    baggageFee: pickBaggageFee(order),
    supplierNet: toNumberOrNull(
      (order.ourprice ?? order.ourPrice)?.ourTotalFare?.priceWithCommission ??
        (order.ourprice ?? order.ourPrice)?.priceWithCommission
    ),
    hrCommission: null,
    paymentsCount: parseLdsOrderPayments(order).length,
    rawTotalCandidates: [],
    rejectedCandidates: backend.rejectedCandidates,
  };

  emitRemainingSsotTrace({
    orderId: String(order.id ?? order._id ?? ""),
    displayTotal: result.displayTotal,
    displayPaid: result.displayPaid,
    displayRemaining: result.displayRemaining,
    sourceUsed: result.sourceUsed,
    hasBackendBlock: backend.hasBackendBlock,
    rejectedCandidates: result.rejectedCandidates,
  });

  return result;
}

/** @deprecated Use resolveLdsPartialPaymentDisplayBalance */
export function resolveLdsPartialSuccessFinancialDisplay(order) {
  const b = resolveLdsPartialPaymentDisplayBalance(order);
  return {
    displayTotal: b.displayTotal,
    displayPaid: b.displayPaid,
    displayRemaining: b.displayRemaining,
    sourceUsed: b.sourceUsed,
    handlingFee: b.handlingFee,
    supplement: b.supplement,
    paymentsCount: b.paymentsCount,
    auditRemaining: b.auditRemaining,
    adminRemaining: b.adminRemaining,
    rawTotalCandidates: b.rawTotalCandidates,
  };
}
